import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type FlashCardRunRegistration = {
  sessionId: string;
  slug: string;
  progressLabel: string;
  /** Scroll/pin the live widget into the stream viewport */
  focus: () => void;
};

type FlashCardRunPublicState = {
  sessionId: string | null;
  slug: string | null;
  progressLabel: string | null;
  paused: boolean;
  /** True when an incomplete run is registered (active or paused) */
  hasActiveRun: boolean;
  /** Session id of the most recently dismissed run (widget matches this) */
  dismissedSessionId: string | null;
};

type FlashCardRunContextValue = {
  state: FlashCardRunPublicState;
  register: (reg: FlashCardRunRegistration) => void;
  updateProgress: (sessionId: string, progressLabel: string) => void;
  unregister: (sessionId: string) => void;
  /** Pause when the user chats mid-run (discuss-this intent) */
  pauseOnUserChat: () => void;
  resume: () => void;
  dismiss: () => void;
};

const FlashCardRunContext = createContext<FlashCardRunContextValue | null>(null);

const emptyState: FlashCardRunPublicState = {
  sessionId: null,
  slug: null,
  progressLabel: null,
  paused: false,
  hasActiveRun: false,
  dismissedSessionId: null,
};

export function FlashCardRunProvider({ children }: { children: ReactNode }) {
  const regRef = useRef<FlashCardRunRegistration | null>(null);
  /** Survives unregister so Strict Mode / effect re-register keeps pause for same session */
  const lastSessionIdRef = useRef<string | null>(null);
  const pausedRef = useRef(false);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [slug, setSlug] = useState<string | null>(null);
  const [progressLabel, setProgressLabel] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [dismissedSessionId, setDismissedSessionId] = useState<string | null>(null);

  const register = useCallback((reg: FlashCardRunRegistration) => {
    const sameSession = lastSessionIdRef.current === reg.sessionId;
    regRef.current = reg;
    lastSessionIdRef.current = reg.sessionId;
    setSessionId(reg.sessionId);
    setSlug(reg.slug);
    setProgressLabel(reg.progressLabel);
    if (!sameSession) {
      pausedRef.current = false;
      setPaused(false);
      setDismissedSessionId((prev) => (prev === reg.sessionId ? null : prev));
    } else {
      setPaused(pausedRef.current);
    }
  }, []);

  const updateProgress = useCallback((id: string, label: string) => {
    if (regRef.current?.sessionId !== id) return;
    regRef.current = { ...regRef.current, progressLabel: label };
    setProgressLabel(label);
  }, []);

  const unregister = useCallback((id: string) => {
    if (regRef.current?.sessionId !== id) return;
    regRef.current = null;
    setSessionId(null);
    setSlug(null);
    setProgressLabel(null);
    // Keep pausedRef + lastSessionIdRef so a remount can restore pause
  }, []);

  const pauseOnUserChat = useCallback(() => {
    if (!regRef.current && !lastSessionIdRef.current) return;
    // Allow pause even if briefly unregistered between effect cleanup/register
    if (!regRef.current && lastSessionIdRef.current) {
      pausedRef.current = true;
      setPaused(true);
      return;
    }
    if (!regRef.current) return;
    pausedRef.current = true;
    setPaused(true);
  }, []);

  const resume = useCallback(() => {
    if (!regRef.current) return;
    pausedRef.current = false;
    setPaused(false);
    requestAnimationFrame(() => {
      regRef.current?.focus();
    });
  }, []);

  const dismiss = useCallback(() => {
    const id = regRef.current?.sessionId ?? lastSessionIdRef.current;
    regRef.current = null;
    lastSessionIdRef.current = null;
    pausedRef.current = false;
    if (id) setDismissedSessionId(id);
    setSessionId(null);
    setSlug(null);
    setProgressLabel(null);
    setPaused(false);
  }, []);

  const value = useMemo<FlashCardRunContextValue>(
    () => ({
      state: {
        sessionId,
        slug,
        progressLabel,
        paused,
        hasActiveRun: sessionId != null,
        dismissedSessionId,
      },
      register,
      updateProgress,
      unregister,
      pauseOnUserChat,
      resume,
      dismiss,
    }),
    [
      sessionId,
      slug,
      progressLabel,
      paused,
      dismissedSessionId,
      register,
      updateProgress,
      unregister,
      pauseOnUserChat,
      resume,
      dismiss,
    ]
  );

  return (
    <FlashCardRunContext.Provider value={value}>{children}</FlashCardRunContext.Provider>
  );
}

export function useFlashCardRun(): FlashCardRunContextValue {
  const ctx = useContext(FlashCardRunContext);
  if (!ctx) {
    return {
      state: emptyState,
      register: () => {},
      updateProgress: () => {},
      unregister: () => {},
      pauseOnUserChat: () => {},
      resume: () => {},
      dismiss: () => {},
    };
  }
  return ctx;
}
